import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import apiClient from '@/services/api-client';
import {
  fetchEmployeeStatusList,
  fetchStatusList,
} from '@/services/tenant/tenant-user-services';
import { ApiResponse } from '@/services/types';
import { RootState } from '@/store';
import { API_ENDPOINTS } from '@/utils/constants/end-points';

export interface TenantInfo {
  id: number;
  domain: string;
  name?: string;
  [key: string]: any;
}

export interface WorkflowStatus {
  id: number;
  name: string;
  code?: string;
  [key: string]: any;
}

export interface Status {
  id: number;
  statusName: string;
}

export interface EmployeeStatus {
  id: number;
  statusName: string;
}

interface TenantState {
  data: TenantInfo | null;
  workflowStatuses: WorkflowStatus[];
  isLoadingWorkflowStatuses: boolean;
  workflowStatusesError: string | null;
  statuses: Status[];
  isLoadingStatuses: boolean;
  statusesError: string | null;
  employeeStatuses: EmployeeStatus[];
  isLoadingEmployeeStatuses: boolean;
  employeeStatusesError: string | null;
}

const initialState: TenantState = {
  data: null,
  workflowStatuses: [],
  isLoadingWorkflowStatuses: false,
  workflowStatusesError: null,
  statuses: [],
  isLoadingStatuses: false,
  statusesError: null,
  employeeStatuses: [],
  isLoadingEmployeeStatuses: false,
  employeeStatusesError: null,
};

export const fetchWorkflowStatuses = createAsyncThunk<
  WorkflowStatus[],
  void,
  { state: RootState; rejectValue: string }
>(
  'tenant/fetchWorkflowStatuses',
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiClient.get<ApiResponse<WorkflowStatus[]>>(
        `${API_ENDPOINTS.MASTER}/workflow-statuses`
      );

      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(
        err?.message || 'Failed to fetch workflow statuses'
      );
    }
  },
  {
    // Every component using useDashboardStatuses dispatches this on mount;
    // one request is enough while it is already on its way.
    condition: (_, { getState }) =>
      !getState?.()?.tenant?.info?.isLoadingWorkflowStatuses,
  }
);

export const fetchStatusesThunk = createAsyncThunk<
  Status[],
  void,
  { rejectValue: string }
>('tenant/fetchStatuses', async (_, { rejectWithValue }) => {
  try {
    const data = await fetchStatusList();
    return data;
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to fetch statuses');
  }
});

export const fetchEmployeeStatusesThunk = createAsyncThunk<
  EmployeeStatus[],
  void,
  { rejectValue: string }
>('tenant/fetchEmployeeStatuses', async (_, { rejectWithValue }) => {
  try {
    const data = await fetchEmployeeStatusList();
    return data;
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to fetch employee statuses');
  }
});

const tenantSlice = createSlice({
  name: 'tenant',
  initialState,
  reducers: {
    setTenantInfo: (state, action: PayloadAction<TenantInfo>) => {
      state.data = action.payload;
    },
    clearTenantInfo: state => {
      state.data = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchWorkflowStatuses.pending, state => {
        state.isLoadingWorkflowStatuses = true;
        state.workflowStatusesError = null;
      })
      .addCase(
        fetchWorkflowStatuses.fulfilled,
        (state, action: PayloadAction<WorkflowStatus[]>) => {
          state.isLoadingWorkflowStatuses = false;
          state.workflowStatuses = action.payload;
        }
      )
      .addCase(fetchWorkflowStatuses.rejected, (state, action) => {
        state.isLoadingWorkflowStatuses = false;
        state.workflowStatusesError = action.payload ?? 'Unknown error';
      })
      .addCase(fetchStatusesThunk.pending, state => {
        state.isLoadingStatuses = true;
        state.statusesError = null;
      })
      .addCase(
        fetchStatusesThunk.fulfilled,
        (state, action: PayloadAction<Status[]>) => {
          state.isLoadingStatuses = false;
          state.statuses = action.payload;
        }
      )
      .addCase(fetchStatusesThunk.rejected, (state, action) => {
        state.isLoadingStatuses = false;
        state.statusesError = action.payload ?? 'Failed to fetch statuses';
      })
      .addCase(fetchEmployeeStatusesThunk.pending, state => {
        state.isLoadingEmployeeStatuses = true;
        state.employeeStatusesError = null;
      })
      .addCase(
        fetchEmployeeStatusesThunk.fulfilled,
        (state, action: PayloadAction<EmployeeStatus[]>) => {
          state.isLoadingEmployeeStatuses = false;
          state.employeeStatuses = action.payload;
        }
      )
      .addCase(fetchEmployeeStatusesThunk.rejected, (state, action) => {
        state.isLoadingEmployeeStatuses = false;
        state.employeeStatusesError =
          action.payload ?? 'Failed to fetch employee statuses';
      });
  },
});

export const { setTenantInfo, clearTenantInfo } = tenantSlice.actions;

export const selectWorkflowStatuses = (state: RootState) =>
  state.tenant.info.workflowStatuses;
export const selectWorkflowStatusesLoading = (state: RootState) =>
  state.tenant.info.isLoadingWorkflowStatuses;
export const selectWorkflowStatusesError = (state: RootState) =>
  state.tenant.info.workflowStatusesError;

export const selectEmployeeStatuses = (state: RootState) =>
  state.tenant.info.employeeStatuses;
export const selectEmployeeStatusesLoading = (state: RootState) =>
  state.tenant.info.isLoadingEmployeeStatuses;
export const selectEmployeeStatusesError = (state: RootState) =>
  state.tenant.info.employeeStatusesError;

export default tenantSlice.reducer;
